import React, { useContext, useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import APIContext from "../../../services/api/api";
import { URLS } from "../../../configuration/urls";

AnnotationsModule(Highcharts);


const defaultData = [
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  },
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  },
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  },
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  },
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  },
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  },
  {
    "date": "",
    "averageThroughput": 0,
    "deviation": -100
  }
];

export function MAWBThroughputChart({ target = 2160, today, id }) {
  const [throughputData, setThroughputData] = useState(defaultData);
  const [isLoading, setIsLoading] = useState(false);
  const { getData } = useContext(APIContext);
  const { formatMessage } = useIntl();
  const [isThroughputVisible, setIsThroughputVisible] = useState(true);

  useEffect(() => {
    async function fetchAllData() {
      setIsLoading(true);
      try {
        const [throughput] = await Promise.all([
          getData(`h7/kpi/mawb/${id}/throughput?currentDate=${today}`, {}, URLS.EC_CLEAR_CUSTOMS_BE())
        ]);
        if (Array.isArray(throughput) && throughput?.length > 0) {
          setThroughputData(throughput);
          return;
        }
        setThroughputData(defaultData);
      } catch (error) {
        console.log("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllData();
  }, [id]);

  const hasValidDeviationData =
    Array.isArray(throughputData) &&
    throughputData.some(
      item =>
        item?.averageThroughput != null &&
        item?.deviation != null &&
        item?.deviation > -100
    );


  const actualValues = Array.isArray(throughputData) ? throughputData?.map(item => item?.averageThroughput || "") : [];
  const deviationPercentages = hasValidDeviationData
    ? throughputData.map(item =>
      item?.deviation != null && item.deviation > -100 ? item.deviation : null
    )
    : [];
  const targetValues = Array.isArray(throughputData) ? throughputData?.map(() => target || "") : [];
  const categories = Array.isArray(deviationPercentages) ? throughputData.map(item =>
      item?.date ? item.date : ""
    )
    : [];

  const annotations =
    hasValidDeviationData && isThroughputVisible
      ? throughputData.map((item, i) => {
          if (deviationPercentages[i] == null) return null;
          return (
            {
              labels: [
                {
                  shape: "connector",
                  allowOverlap: false,
                  point: {
                    align: "top",
                    xAxis: 0,
                    yAxis: 0,
                    x: i,
                    y: target
                  },
                  text: `${deviationPercentages[i] > 0 ? "+" : ""}${deviationPercentages[i] || ""}%`,
                  borderColor: "#FF9800",
                  style: {
                    color: "var(--button-subtle-text)",
                    fontWeight: "bold",
                    fontSize: "14px"
                  },
                  y: -10,
                  x: -100
                }
              ]
            }
          );
        }
      ).filter(Boolean)
      : [];


  const options = {
    chart: {
      type: "column",
      height: 400
    },
    title: { text: null },
    xAxis: {
      categories,
      borderWidth: 100,
      lineWidth: 4,
      lineColor: "var(--text-secondary)",
      labels: { enabled: false }
    },
    yAxis: {
      min: 0,
      title: { text: "Minutes" },
      visible: false
    },
    tooltip: {
      enabled: false
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      itemStyle: { fontWeight: "bold", fontSize: "20px" }
    },
    credits: { enabled: false },
    plotOptions: {
      column: {
        grouping: false,
        allowPointSelect: true,
        borderWidth: 0
      }
    },
    series: [
      {
        name: `<span style=\"font-size:20px;color:var(--text-primary);\">${formatMessage({ id: "accumulatedAverageThroughputTime" })}</span> <span style=\"font-size:14px;color:var(--text-primary);\">(${formatMessage({ id: "minutes" })})</span>`,
        data: hasValidDeviationData ? actualValues : [],
        color: "var(--warning-100)",
        fontSize: 23,
        pointPadding: 0.2,
        dataLabels: {
          enabled: true,
          distance: 40,
          formatter() {
            return handleNumberConverter({ value: this.y, fraction: 0 });
          },
          style: {
            fontWeight: "bold",
            fontSize: "16px",
            textOutline: "0px none",
            color: "var(--button-subtle-text)"
          },
          inside: false,
          verticalAlign: "bottom"
        }
      },
      {
        id: "deviation-throughput",
        name: `<span style=\"font-size:20px;color:var(--text-primary);\">${formatMessage({ id: "deviationFromTarget" })}</span> <span style=\"font-size:14px;color:var(--text-primary);\">(${target} ${formatMessage({ id: "minutes" })})</span>`,
        data: isThroughputVisible && targetValues.length > 0 ? targetValues : [0],
        color: isThroughputVisible ? "rgba(255,165,0,0.3)" : "rgba(204, 204, 204)",
        pointPadding: 0.15,
        borderWidth: 0,
        events: {
          legendItemClick: function() {
            setIsThroughputVisible(prev => !prev);
            return false;
          }
        }
      }
    ],
    annotations: annotations
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 2, overflow: "hidden" }} backgroundColor="var(--container-light)">
      <Box
        backgroundColor="var(--container-light)"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "2px solid",
          borderColor: "#e8ebef",
          py: 1,
          px: 2
        }}
      >
        <Typography
          data-testid={`throughput_box_heading`}
          color={"var(--text-primary)"} variant="subtitle1" fontWeight={600}
          fontSize={"20px"}>
          <FormattedMessage id="accumulatedAverageThroughputTime" />
          {" "}(<FormattedMessage id="minutes" />)
        </Typography>
      </Box>
      {!Array.isArray(deviationPercentages) &&
        <Typography
          style={{
            textAlign: "center",
            padding: "3rem"
          }}
          variant="subtitle1"
        >
          <FormattedMessage id="noData" />
        </Typography>
      }
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
}


function handleNumberConverter({ value, fraction = 0 }) {
  return Number(value)?.toLocaleString?.("de-DE", {
    minimumFractionDigits: fraction
  });
}
